import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import {
  ExpandableTrackingWrapper,
  DeliveryPhotoSection,
  DeliveryPhotoTitle,
  DeliveryPhotoInputRow,
  DeliveryPhotoPostalInputWrapper,
  DeliveryPhotoPostalInput,
  DeliveryPhotoError,
} from './css';
import { apiGetOrderById } from '../../../services/orders';
import downloadDocument from '../../../components/ViewOrder/PODDocuments/api';
import {
  Table,
  TableCell,
  CellLabel,
  CellValueLink,
  TableDivider,
} from '../../../components/ViewOrder/css';
import Loader from '../loader';
import Tracking from '../maps/Tracking';
import PrimaryButton from '../buttons/PrimaryButton';

// Strip spaces and uppercase so "P7J 1K4" and "P7J1K4" compare equal
const normalisePostal = (value) => (value || '').replace(/\s+/g, '').toUpperCase();

const ExpandableTracking = (props) => {
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState([]);
  const [orderData, setOrderData] = useState(null);

  // Delivery photo state
  const [postalCode, setPostalCode] = useState('');
  const [postalError, setPostalError] = useState('');
  const [safeDropPhotos, setSafeDropPhotos] = useState(null);

  const {
    data, expanderData, setExpanderData, values,
  } = props;
  const rowId = data.id;
  const rowValues = values;

  const orderId = (rowValues.orderId) ? rowValues.orderId : rowValues.probillNumber;

  const filterTrackingAfterDelivered = (trackingData) => {
    if (!trackingData || trackingData.length === 0) {
      return trackingData;
    }

    let deliveredEventIndex = -1;
    let deliveredTouchPointIndex = -1;
    let latestDeliveredTime = null;

    for (let i = 0; i < trackingData.length; i++) {
      const event = trackingData[i];
      if (event.touchPoints && event.touchPoints.length > 0) {
        for (let j = 0; j < event.touchPoints.length; j++) {
          const touchPoint = event.touchPoints[j];
          const status = touchPoint.status?.toLowerCase() || '';

          if (status.includes('delivered') || status.includes('livré') || status.includes('order canceled') || status.includes('commande annulée')) {
            const currentTime = touchPoint.time;

            if (deliveredEventIndex === -1
              || (currentTime && (!latestDeliveredTime || new Date(currentTime) >= new Date(latestDeliveredTime)))) {
              deliveredEventIndex = i;
              deliveredTouchPointIndex = j;
              latestDeliveredTime = currentTime;
            }
          }
        }
      }
    }

    if (deliveredEventIndex === -1) {
      return trackingData;
    }

    const filteredTracking = trackingData.slice(0, deliveredEventIndex + 1);

    if (filteredTracking.length > 0) {
      const lastEvent = filteredTracking[filteredTracking.length - 1];
      lastEvent.touchPoints = lastEvent.touchPoints.slice(0, deliveredTouchPointIndex + 1);
    }

    return filteredTracking;
  };

  const getTrackingData = () => {
    apiGetOrderById(orderId).then((res) => {
      const filteredTracking = filterTrackingAfterDelivered(res.data.tracking);
      setOrderData(res.data);
      const updatedData = [...expanderData];
      updatedData.push({
        id: rowId, orderId, events: filteredTracking, orderData: res.data,
      });
      setExpanderData(updatedData);
    });
  };

  useEffect(() => {
    const trackingObj = expanderData.filter((row) => row.id === rowId);
    if (trackingObj.length === 0) {
      getTrackingData(orderId);
    } else {
      setTracking(trackingObj);

      // Restore orderData from cache so postal validation works on re-expand
      if (trackingObj[0].orderData) {
        setOrderData(trackingObj[0].orderData);
      }

      setLoading(false);
    }
  }, [expanderData]);

  const handlePostalChange = (e) => {
    const { value } = e.target;
    setPostalCode(value);
    setSafeDropPhotos(null);

    if (!value) {
      setPostalError('');
      return;
    }

    const consigneePostal = orderData && orderData.to && orderData.to.postalCode;
    const enteredNorm = normalisePostal(value);
    const recordNorm = normalisePostal(consigneePostal);

    if (enteredNorm !== recordNorm) {
      setPostalError(t('PostalCodeError'));
    } else {
      setPostalError('');
    }
  };

  const handleViewPhoto = () => {
    const documents = (orderData && orderData.documents) || [];
    const photos = documents.filter((doc) => doc.fileType === 'Safe_Drop_Photo');
    setSafeDropPhotos(photos);
  };

  if (loading) {
    return (
      <ExpandableTrackingWrapper>
        <Loader isLoading={loading} />
      </ExpandableTrackingWrapper>
    );
  }

  return (
    <ExpandableTrackingWrapper>
      <Tracking data={tracking[0].events} />

      <DeliveryPhotoSection>
        <DeliveryPhotoTitle>{t('CheckForDeliveryPhoto')}</DeliveryPhotoTitle>
        <DeliveryPhotoInputRow>
          <DeliveryPhotoPostalInputWrapper>
            <DeliveryPhotoPostalInput
              type="text"
              placeholder={t('ConsigneePostalCode')}
              value={postalCode}
              onChange={handlePostalChange}
              hasError={!!postalError}
              aria-label={t('ConsigneePostalCode')}
            />
            {postalError && (
              <DeliveryPhotoError>{postalError}</DeliveryPhotoError>
            )}
          </DeliveryPhotoPostalInputWrapper>
          <PrimaryButton
            type="button"
            disabled={
              !postalCode
              || normalisePostal(postalCode) !== normalisePostal(orderData && orderData.to && orderData.to.postalCode)
            }
            onClick={handleViewPhoto}
          >
            {t('ViewPhoto')}
          </PrimaryButton>
        </DeliveryPhotoInputRow>

        {safeDropPhotos && safeDropPhotos.length > 0 && (
          <Table>
            {safeDropPhotos.map(({ documentId, fileType }) => (
              <React.Fragment key={documentId}>
                <TableCell>
                  <CellLabel>{fileType}</CellLabel>
                  <CellValueLink
                    onClick={(e) => {
                      e.preventDefault();
                      downloadDocument(documentId, null, documentId);
                    }}
                  >
                    {t('View')}
                  </CellValueLink>
                </TableCell>
                <TableDivider />
              </React.Fragment>
            ))}
          </Table>
        )}

        {safeDropPhotos && safeDropPhotos.length === 0 && (
          <DeliveryPhotoError>{t('NoPhotoAvailable')}</DeliveryPhotoError>
        )}
      </DeliveryPhotoSection>
    </ExpandableTrackingWrapper>
  );
};

ExpandableTracking.propTypes = {
  data: PropTypes.object,
  expanderData: PropTypes.array,
  setExpanderData: PropTypes.func,
  values: PropTypes.object,
};

export default ExpandableTracking;
